import type { SxProps } from "@/types";
import { cloneElementWithCssProp, mergeSx, prettyFormatNumber } from "@/utils";
import type { BodySize, BodyWeight } from "@biom3/design-tokens";
import type { Properties } from "csstype";
import { type ReactElement, type ReactNode, type Ref, useMemo } from "react";

import { Box } from "@/components/Box";
import { SmartClone } from "@/components/SmartClone";
import { Body } from "@/components/Text/Body";
import { useGetSubcomponentChild } from "@/hooks";
import type { GetMultiTextProps, StandardComponentWithProps } from "@/types";

import { PriceDisplayCaption } from "./PriceDisplayCaption";
import { PriceDisplayCurrencyImage } from "./PriceDisplayCurrencyImage";
import { PriceDisplayIcon } from "./PriceDisplayIcon";
import { baseImageSx, baseOuterContainerSx, priceContentSx } from "./styles";

type PriceDisplayBaseProps = StandardComponentWithProps<
  HTMLSpanElement,
  {
    /**
     * @deprecated Support for the `fiatAmount` prop will be removed in the future.
     * Please use the `<PriceDisplay.Caption />` subcomponent instead.
     */
    fiatAmount?: string;
    price: string;
    use?: ReactElement<unknown>;
    children?: ReactNode;
  }
>;

export type PriceDisplayProps<
  RC extends ReactElement | undefined,
  Use extends ReactElement | undefined,
> = Omit<GetMultiTextProps<RC, Use, PriceDisplayBaseProps>, "shimmer"> & {
  shimmer?: boolean;
};

const DEFAULT_RC = <span />;

export function PriceDisplay<
  RC extends ReactElement | undefined,
  Use extends ReactElement | undefined = undefined,
>({
  price,
  domRef,
  sx,
  testId = "PriceDisplay",
  rc = DEFAULT_RC,
  className,
  children,
  shimmer,
  fiatAmount,
  ...props
}: PriceDisplayProps<RC, Use>) {
  const { use, ...propsMinusUse } =
    "use" in props ? props : { ...props, use: undefined };
  const { size, ...propsMinusUseAndSize } =
    "size" in propsMinusUse
      ? propsMinusUse
      : { ...propsMinusUse, size: undefined };
  const { weight, ...boxDomAttrProps } =
    "weight" in propsMinusUseAndSize
      ? propsMinusUseAndSize
      : { ...propsMinusUseAndSize, weight: "bold" };

  const prettyPrice = prettyFormatNumber(price);
  const currencyImage = useGetSubcomponentChild(
    children,
    PriceDisplayCurrencyImage,
  );
  const caption = useGetSubcomponentChild(children, PriceDisplayCaption);
  const icon = useGetSubcomponentChild(children, PriceDisplayIcon);
  const pickedPriceImagery = currencyImage ?? icon ?? null;
  const pickedPriceImagerySx = pickedPriceImagery?.props?.sx ?? {};

  let pickedSxColor: SxProps;
  if (Array.isArray(sx)) {
    const fill = (sx.find((s) => s.c)?.c ??
      sx.find((s) => s.color)?.color ??
      "base.color.text.body.primary") as Properties["fill"];
    pickedSxColor = { fill };
  } else {
    const fill = sx?.c ?? sx?.color ?? "base.color.text.body.primary";
    pickedSxColor = { fill: fill as Properties["fill"] };
  }

  const mergedCurrencyImageSx = useMemo(
    () => mergeSx(baseImageSx, pickedSxColor, pickedPriceImagerySx),
    [pickedPriceImagerySx, pickedSxColor],
  );
  const priceContent = useMemo(
    () =>
      cloneElementWithCssProp(
        use || <Body size={size as BodySize} weight={weight as BodyWeight} />,
        {
          testId: `${testId}__price`,
          ...(size ? { size } : {}),
          sx: priceContentSx,
          shimmer: shimmer ? 1 : 0,
          children: (
            <>
              {prettyPrice}
              {currencyImage ? (
                <SmartClone
                  testId={`${testId}__price__currencyImage`}
                  sx={mergedCurrencyImageSx}
                  aspectRatio={currencyImage.props?.aspectRatio ?? "1"}
                >
                  {currencyImage}
                </SmartClone>
              ) : icon ? (
                <SmartClone
                  testId={`${testId}__price__icon`}
                  sx={mergedCurrencyImageSx}
                >
                  {icon}
                </SmartClone>
              ) : null}
            </>
          ),
          className: "priceContent",
        },
      ),
    [
      currencyImage,
      mergedCurrencyImageSx,
      icon,
      prettyPrice,
      size,
      testId,
      use,
      shimmer,
      weight,
    ],
  );

  const fiatAmountCaption = useMemo(() => {
    if (!caption && !fiatAmount) return null;

    return fiatAmount ? (
      <PriceDisplayCaption
        size="xSmall"
        weight="regular"
        testId={`${testId}__fiatAmount`}
        shimmer={shimmer ? 1 : 0}
      >
        {fiatAmount}
      </PriceDisplayCaption>
    ) : (
      <SmartClone shimmer={shimmer ? 1 : 0}>{caption}</SmartClone>
    );
  }, [caption, testId, fiatAmount, shimmer]);

  return (
    <Box
      {...props}
      testId={testId}
      rc={rc}
      // @TODO: this should be a Span element, not HTMLDivElement
      domRef={domRef as Ref<HTMLDivElement>}
      sx={mergeSx(baseOuterContainerSx, sx)}
      className={`${className ?? ""} PriceDisplay`}
    >
      {priceContent}
      {fiatAmountCaption}
    </Box>
  );
}

PriceDisplay.displayName = "PriceDisplay";
PriceDisplay.CurrencyImage = PriceDisplayCurrencyImage;
PriceDisplay.Caption = PriceDisplayCaption;
PriceDisplay.Icon = PriceDisplayIcon;
